import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type BlockedUser = { id: string; displayName: string; profileImageUrl: string | null };

export default function BlockedAccounts() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: blocked, isLoading } = useQuery<BlockedUser[]>({
    queryKey: ["/api/me/blocked"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const unblock = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", "/api/unblock", { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/me/blocked"] });
      toast({ title: "Unblocked" });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't unblock", description: e?.message, variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={() => setLocation("/account")}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Blocked accounts</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Blocked people can't message you, and you won't see their listings or reviews.
        </p>

        {isLoading && (
          <div className="flex items-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!isLoading && (!blocked || blocked.length === 0) && (
          <p className="py-10 text-sm text-muted-foreground">You haven't blocked anyone.</p>
        )}

        <div className="mt-4 divide-y">
          {blocked?.map((u) => (
            <div key={u.id} className="flex items-center gap-3 py-3">
              <div className="h-9 w-9 overflow-hidden rounded-full bg-muted">
                {u.profileImageUrl && (
                  <img src={u.profileImageUrl} alt="" className="h-full w-full object-cover" />
                )}
              </div>
              <span className="flex-1 text-sm font-medium">{u.displayName}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => unblock.mutate(u.id)}
                disabled={unblock.isPending}
              >
                Unblock
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
